import { createReducer } from '../../utils/createReducer';
import { createNewMessage, createLinkSnippet, createComponentMessage } from '../../utils/messages';
import { MESSAGE_SENDER } from '../../constants';
import {
  ADD_NEW_USER_MESSAGE,
  ADD_NEW_RESPONSE_MESSAGE,
  ADD_NEW_LINK_SNIPPET,
  ADD_COMPONENT_MESSAGE,
  DROP_MESSAGES,
  HIDE_AVATAR,
  DELETE_MESSAGES,
  MARK_ALL_READ,
  SET_BADGE_COUNT,
  RESET_MESSAGES,
  ADD_HISTORY_MESSAGE,
  ADD_HISTORY_COMPONENT,
  SET_WIDGET_CONFIG,
  SET_IS_CHAT_FULL_SCREEN,
  SET_SHOW_SENDER_COMPONENT,
  DELETE_LAST_MESSAGE,
  SET_RECENT_MESSAGE,
  SET_SESSION_AVAILABLE,
  SET_TOTAL_MESSAGE_COUNT,
  SET_SESSION_STARTED,
  SET_LOADING_MESSAGES,
} from '../actions/types';

const initialState = {
  loadingMessages: false,
  messages: [],
  totalMessageCount: 0,
  badgeCount: 0,
  widgetConfig: {},
  showSenderComponent: false,
  recentMessage: {},
  isSessionAvailable: false,
  isChatFullScreen: false,
  isSessionStarted: false,
};

const messagesReducer = {
  [ADD_NEW_USER_MESSAGE]: (state, { text, showClientAvatar, id }) => {
    return {
      ...state,
      // messages: [...state.messages, createNewMessage(text, MESSAGE_SENDER.CLIENT, id)],
      messages: [createNewMessage(text, MESSAGE_SENDER.CLIENT, id), ...state.messages],
    };
  },

  [ADD_NEW_RESPONSE_MESSAGE]: (state, { text, id }) => ({
    ...state,
    // messages: [...state.messages, createNewMessage(text, MESSAGE_SENDER.RESPONSE, id)],
    messages: [createNewMessage(text, MESSAGE_SENDER.RESPONSE, id), ...state.messages],
    badgeCount: state.badgeCount + 1,
  }),

  [ADD_NEW_LINK_SNIPPET]: (state, { link, id }) => ({
    ...state,
    // messages: [...state.messages, createLinkSnippet(link, id)],
    messages: [createLinkSnippet(link, id), ...state.messages],
  }),
  [ADD_COMPONENT_MESSAGE]: (state, { component, props, showAvatar, id }) => {
    const targetId = props?.message?.id;
    const isStreamingMode = props?.message?.mode === 'continue' || props?.message?.mode === 'stop';

    if ((isStreamingMode || props?.message?.mode === 'replace') && targetId) {
      const existingIndex = state.messages.findIndex((msg) => msg?.props?.message?.id === targetId);

      // 🟢 If message already exists, merge streaming text
      if (existingIndex !== -1) {
        const existing = state.messages[existingIndex];
        const existingText = existing?.props?.message?.content?.text || '';
        const incomingText = props?.message?.content?.text || '';

        // Merge text and update in-place
        state.messages[existingIndex] = {
          ...existing,
          props: {
            ...existing?.props,
            message: {
              ...existing?.props?.message,
              content: {
                ...existing?.props?.message?.content,
                text: isStreamingMode
                  ? existingText + incomingText
                  : props?.message?.mode === 'replace'
                    ? incomingText
                    : '',
              },
            },
          },
        };

        return {
          ...state,
          messages: [...state.messages],
          recentMessage: state.messages[existingIndex]?.props?.message,
        };
      }
    }

    // 🟡 No existing message: Add new
    return {
      ...state,
      // messages: [...state.messages, createComponentMessage(component, props, showAvatar, id)],
      messages: [createComponentMessage(component, props, showAvatar, id), ...state.messages],
    };
  },

  // [ADD_COMPONENT_MESSAGE]: (state, { component, props, showAvatar, id }) => {
  //   const nodeLength = 0;
  //   console.log(state.messages);
  //   console.log(props);
  //   if (
  //     // nodeLength >= 0 &&
  //     (props?.message?.mode === 'continue' || props?.message?.mode === 'stop') &&
  //     state?.messages &&
  //     state.messages[nodeLength] &&
  //     state?.messages[nodeLength]?.props &&
  //     state?.messages[nodeLength]?.props?.message &&
  //     state?.messages[nodeLength]?.props?.message?.content
  //   ) {
  //     state.messages[nodeLength].props.message.content.text =
  //       state?.messages[nodeLength]?.props?.message?.content?.text + props?.message?.content?.text;
  //     return {
  //       ...state,
  //       messages: [...state.messages],
  //       recentMessage: state.messages[nodeLength].props.message,
  //     };
  //   }
  //   return {
  //     ...state,
  //     messages: [createComponentMessage(component, props, showAvatar, id), ...state.messages],
  //   };
  // },
  [ADD_HISTORY_MESSAGE]: (state, { component, props, showAvatar, id }) => {
    const multiMessage = Array.isArray(state.messages) ? state.messages : [];
    const newMessages = component.map(({ component, props, showAvatar, id }) =>
      createComponentMessage(component, props, showAvatar, id)
    );
    return {
      ...state,
      // messages: [...newMessages, multiMessage],
      messages: [multiMessage, ...newMessages],
    };
  },
  [ADD_HISTORY_COMPONENT]: (state, { component, props, showAvatar, id }) => {
    const existingMessages = Array.isArray(state.messages) ? state.messages : [];
    return {
      ...state,
      // messages: [createComponentMessage(component, props, showAvatar, id), ...existingMessages],
      messages: [...existingMessages, createComponentMessage(component, props, showAvatar, id)],
    };
  },

  [DROP_MESSAGES]: (state) => ({ ...state, messages: [] }),

  [HIDE_AVATAR]: (state, { index }) => {
    const updatedMessages = [...state.messages];
    updatedMessages[index].showAvatar = false;
    return { ...state, messages: updatedMessages };
  },

  [DELETE_MESSAGES]: (state, { count, id }) => ({
    ...state,
    messages: id
      ? state.messages.filter((_, index) => {
          const targetMsgIndex = state.messages.findIndex((tMsg) => tMsg.customId === id);
          return index < targetMsgIndex - count + 1 || index > targetMsgIndex;
        })
      : state.messages.slice(0, state.messages.length - count),
  }),

  [DELETE_LAST_MESSAGE]: (state) => ({
    ...state,
    messages: state.messages.slice(0, -1),
  }),

  [SET_TOTAL_MESSAGE_COUNT]: (state, { count }) => ({ ...state, totalMessageCount: count }),

  [SET_BADGE_COUNT]: (state, { count }) => ({ ...state, badgeCount: count }),

  [RESET_MESSAGES]: (state, { value }) => ({ ...state, messages: value }),

  [MARK_ALL_READ]: (state) => ({
    ...state,
    messages: state.messages.map((message) => ({ ...message, unread: false })),
    badgeCount: 0,
  }),

  [SET_WIDGET_CONFIG]: (state, { data }) => ({ ...state, widgetConfig: data }),

  [SET_LOADING_MESSAGES]: (state, { cond }) => ({ ...state, loadingMessages: cond }),

  [SET_IS_CHAT_FULL_SCREEN]: (state, { cond }) => ({ ...state, isChatFullScreen: cond }),

  [SET_SESSION_AVAILABLE]: (state, { cond }) => ({ ...state, isSessionAvailable: cond }),

  [SET_SESSION_STARTED]: (state, { cond }) => ({ ...state, isSessionStarted: cond }),

  [SET_SHOW_SENDER_COMPONENT]: (state, { cond }) => ({ ...state, showSenderComponent: cond }),

  [SET_RECENT_MESSAGE]: (state, { val }) => ({ ...state, recentMessage: val }),
};

export default function messages(state = initialState, action) {
  return createReducer(messagesReducer, state, action);
}
